import { IKeyObjective } from 'legacy/lut/classes/models/partner360/IFormOptionsJBP'
import { DESCRIPTION_QUALITY_CONFIG } from 'legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives/commonComponents/editorComponents/components/descriptionQuality/config'
import { isDescriptionQualityBlocking } from 'legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives/commonComponents/editorComponents/components/descriptionQuality/helpers'
import {
  EKeyInitiativeGoalMode,
  getPersistedKeyInitiativeGoalMode,
} from 'legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives/lpKeyInitiatives/components/keyInitiativeEditorModal/helpers/keyInitiativeGoalMode'
import { IKIBusinessPriority } from 'legacy/lut/features/partner360/jbpSettings/models/kiBusinessPriority'

import { KeyInitiative } from 'partner360/entities/keyInitiatives'
import { isKeyInitiativeStatus } from 'partner360/entities/keyInitiatives/model/status'
import {
  hasKeyInitiativeBusinessPriorities,
  isKeyInitiativeBusinessPrioritiesRequired,
  isKeyInitiativeBusinessPrioritiesVisible,
} from 'partner360/features/keyInitiativeEditor/lib/keyInitiativeBusinessPriorities'
import {
  getKeyInitiativeTargetValueType,
  isValidKeyInitiativeFinancialTarget,
} from 'partner360/features/keyInitiativeEditor/lib/keyInitiativeFinancialTarget'
import { isValidKeyInitiativeFundingAmount } from 'partner360/features/keyInitiativeEditor/lib/keyInitiativeMdfFunding'
import { MAX_INITIATIVE_NAME_LENGTH } from 'partner360/features/keyInitiativeEditor/ui/lib/formFieldSources'

import {
  CopyValidationIssue,
  ICopyValidationResult,
  IDescriptionQualityCheckResult,
} from '../model/types'

const VALID_PERIODS = new Set(['Q1', 'Q2', 'Q3', 'Q4'])

interface ICopyValidationDictionaries {
  strategicInitiatives: ReadonlySet<string>
  businessUnits: ReadonlySet<string>
  keyObjectivesForYear: readonly IKeyObjective[]
  availableBusinessPriorities: readonly IKIBusinessPriority[]
}

interface IValidateKeyInitiativeForCopyParams extends ICopyValidationDictionaries {
  initiative: KeyInitiative
  descriptionQuality: IDescriptionQualityCheckResult | null
  isDescriptionQualityUnavailable?: boolean
  defaultValueType?: string
}

const hasValidPeriods = (period?: string | null): boolean => {
  const values =
    period
      ?.split(',')
      .map((value) => value.trim())
      .filter(Boolean) ?? []

  return values.length > 0 && values.every((value) => VALID_PERIODS.has(value))
}

const hasValidFunding = (initiative: KeyInitiative): boolean => {
  const hasSource = Boolean(initiative.HPInvestmentSource?.trim())
  const hasAmount = initiative.HPInvestmentSum !== null && initiative.HPInvestmentSum !== undefined

  // The checkbox is editor-only; use the same saved-data inference as the editor.
  const isInvestmentNeeds = [initiative.HPInvestmentSource, initiative.HPInvestmentSum].some(
    Boolean
  )
  if (!isInvestmentNeeds) {
    return true
  }

  return hasSource && hasAmount && isValidKeyInitiativeFundingAmount(initiative.HPInvestmentSum)
}

export const getEmptyDescriptionQuality = (): IDescriptionQualityCheckResult => ({
  score: DESCRIPTION_QUALITY_CONFIG.defaultValue.score,
  reasons: [...DESCRIPTION_QUALITY_CONFIG.defaultValue.reasons],
})

export const validateKeyInitiativeForCopy = ({
  initiative,
  strategicInitiatives,
  businessUnits,
  keyObjectivesForYear,
  availableBusinessPriorities,
  descriptionQuality,
  isDescriptionQualityUnavailable = false,
  defaultValueType,
}: IValidateKeyInitiativeForCopyParams): ICopyValidationResult => {
  const issues: CopyValidationIssue[] = []
  const name = initiative.Name?.trim() ?? ''
  const description = initiative.Description?.trim() ?? ''
  const strategicInitiative = initiative.Development?.trim() ?? ''
  const businessUnit = initiative.BusinessUnit?.trim() ?? ''

  if (!name) {
    issues.push(CopyValidationIssue.NameRequired)
  } else if (name.length > MAX_INITIATIVE_NAME_LENGTH) {
    issues.push(CopyValidationIssue.NameTooLong)
  }

  if (!description) {
    issues.push(CopyValidationIssue.DescriptionRequired)
  }

  if (
    strategicInitiatives.size > 0 &&
    (!strategicInitiative || !strategicInitiatives.has(strategicInitiative))
  ) {
    issues.push(CopyValidationIssue.StrategicInitiative)
  }

  if (businessUnit && !businessUnits.has(businessUnit)) {
    issues.push(CopyValidationIssue.BusinessUnit)
  }

  // Keep MDF/visibility validation in sync with the step editor:
  // legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives/lpKeyInitiatives/components/keyInitiativeEditorModal/components/keyInitiativeStepsContent/KeyInitiativeStepsContent.tsx.
  const businessPriorities = initiative.BusinessPriorities?.split(',') ?? []
  const isBusinessPrioritiesVisible =
    initiative.IsNotApplicable !== 1 &&
    isKeyInitiativeBusinessPrioritiesVisible(availableBusinessPriorities, businessPriorities)
  if (
    isKeyInitiativeBusinessPrioritiesRequired(
      strategicInitiative,
      keyObjectivesForYear,
      isBusinessPrioritiesVisible
    ) &&
    !hasKeyInitiativeBusinessPriorities(businessPriorities)
  ) {
    issues.push(CopyValidationIssue.BusinessPriorities)
  }

  if (!hasValidPeriods(initiative.Period)) {
    issues.push(CopyValidationIssue.Period)
  }

  if (!isKeyInitiativeStatus(initiative.Status)) {
    issues.push(CopyValidationIssue.Status)
  }

  if (!hasValidFunding(initiative)) {
    issues.push(CopyValidationIssue.Funding)
  }

  const goalMode = getPersistedKeyInitiativeGoalMode(initiative)
  const valueType = getKeyInitiativeTargetValueType(initiative.ValueType, defaultValueType)
  const targetField =
    goalMode === EKeyInitiativeGoalMode.Incremental
      ? (`Incremental${valueType}` as const)
      : (`Goal${valueType}` as const)

  if (
    goalMode !== EKeyInitiativeGoalMode.NoSalesTarget &&
    !isValidKeyInitiativeFinancialTarget(initiative[targetField])
  ) {
    issues.push(CopyValidationIssue.FinancialTarget)
  }

  if (initiative.IsFromGlobalPartner === 1 && !initiative.ActivityType?.trim()) {
    issues.push(CopyValidationIssue.ActivityType)
  }

  if (isDescriptionQualityUnavailable || !descriptionQuality) {
    issues.push(CopyValidationIssue.DescriptionQualityUnavailable)
  } else if (isDescriptionQualityBlocking(descriptionQuality)) {
    issues.push(CopyValidationIssue.DescriptionQuality)
  }

  return {
    isValid: issues.length === 0,
    issues,
    descriptionQuality,
  }
}
